<template>
  <div class="app">
    <router-view />
    <VNewVersion
      v-model:is-modal-show="isModalShow"
      @cancel="isModalShow = false"
      @submit="handleModalSubmit()"
    />
  </div>
</template>

<script lang="ts" setup>
import { provide, ref, readonly, onMounted, onUnmounted, computed } from "vue"
import { useResizeObserver } from "@vueuse/core"
import { useToast } from "vue-toastification"
import { VNewVersion } from "@vsimple/vsimple-ui"
import Pusher from "pusher-js"
import { useChunkLoadError } from "@/composables/useChunkLoadError"
import { CHUNK_LOAD_ERROR_TOAST_MESSAGE, CHUNK_LOAD_ERROR_TOAST_TIMEOUT_MS } from "@/constants/CHUNK_LOAD"

const width = ref<number>(window.innerWidth)
useResizeObserver(document.body, (entries) => {
  window.requestAnimationFrame(() => {
    if (!Array.isArray(entries) || !entries.length) {
      return
    }

    const entry = entries[0]
    const { width: contentRectWidth, height } = entry.contentRect
    width.value = contentRectWidth
    document.documentElement.style.setProperty('--vh', `${height * 0.01}px`)
    document.documentElement.style.setProperty('--100vh', `${height}px`)
  })
})

const mobileDetect = computed(() => width.value < 768)
const smallLaptopDetect = computed(() => width.value <= 1440)
const tabletDetect = computed(() => width.value < 1024)

provide('mobileDetect', readonly(mobileDetect))
provide('smallLaptopDetect', readonly(smallLaptopDetect))
provide('tabletDetect', readonly(tabletDetect))

const interval = ref(null)

const isNewVersionAvailable = async (): Promise<boolean> => {
  const req = new Request('/build-info.json', {
    method: 'GET',
    cache: 'no-store',
  })

  const data = await (await fetch(req)).json()
  return data.buildHash !== import.meta.env.VITE_APP_RELEASE_HASH
}

const checkNewVersionAvailable = () => {
  isNewVersionAvailable().then((isNewVersion) => {
    if (isNewVersion) {
      isModalShow.value = true
    }
  })
}

const setCheckNewVersionInterval = () => {
  if (interval.value) {
    clearInterval(interval.value)
  }

  interval.value = setInterval(checkNewVersionAvailable, 5 * 60 * 1000)
}

onMounted(() => {
  if (import.meta.env.VITE_APP_ENV !== 'development') {
    checkNewVersionAvailable()
    setCheckNewVersionInterval()
  }
})
const pusher = new Pusher(
  import.meta.env.VITE_APP_RELEASE_NOTIFIER_PUSHER_APP_KEY,
  {
    cluster: import.meta.env.VITE_APP_RELEASE_NOTIFIER_PUSHER_APP_CLUSTER,
    enabledTransports: ['ws'],
    forceTLS: true,
  },
)

provide('pusher', pusher)

if (import.meta.env.VITE_APP_ENV !== 'development') {
  pusher.subscribe(import.meta.env.VITE_APP_RELEASE_NOTIFIER_PUSHER_CHANNEL)

  pusher.bind(import.meta.env.VITE_APP_RELEASE_NOTIFIER_PUSHER_VSIMPLE_FRONTAPP_RELEASE_EVENT, () => {
    isModalShow.value = true
    setCheckNewVersionInterval()
  })
}

onUnmounted(() => {
  clearInterval(interval.value)
})

const isModalShow = ref<boolean>(false)
const handleModalSubmit = () => {
  window.location.reload()
}

// A chunk that fails to load leaves part of the UI empty until the page is
// reloaded. If a new release explains it, offer the release reload prompt;
// otherwise (same release, or build-info.json unreadable) say what happened
// without claiming a new version. Only once per page load: every further
// failing import would repeat it.
const toast = useToast()
let isChunkLoadErrorHandled = false
useChunkLoadError(async () => {
  if (isChunkLoadErrorHandled) {
    return
  }
  isChunkLoadErrorHandled = true

  const isNewVersion = await isNewVersionAvailable().catch(() => false)
  if (isNewVersion) {
    isModalShow.value = true
    return
  }
  toast.warning(CHUNK_LOAD_ERROR_TOAST_MESSAGE, { timeout: CHUNK_LOAD_ERROR_TOAST_TIMEOUT_MS })
})
</script>

<style lang="scss">
@use "@/scss/main";

.app {
  @media (max-width: 768px) and (max-height: 400px) {
    min-height: calc(100vh + 191px);
  }
  @media (min-width: 769px) and (max-height: 400px) {
    min-height: calc(100vh + 138px);
  }
}
</style>
